import { Component, inject, Input, OnInit, ViewChild, AfterViewInit, signal, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MultipleBarComponent } from '../../../../../shared/components/charts/bar-charts/multiple-bar/multiple-bar.component';
import { DropdownComponent, DropdownOption } from '../../../../../shared/components/ui/dropdown/dropdown.component';
import { ThemeService, Theme } from '../../../../../core/services/theme.service';
import { ApiService } from '../../../../../core/services/api.service';
import { DateRangePickerOutput } from '../../../../../shared/components/date-picker/date-picker.component';
import { IndividualToasterComponent } from "../../../../../shared/components/loader/loader.component";

interface YearData {
  year: number;
  stats: {
    total: number;
    dental: number;
    healthcare: number;
    non_healthcare: number;
  };
  data: MonthData[];
}

interface MonthData {
  start: string;
  end: string;
  total: number;
  dental: number;
  healthcare: number;
  non_healthcare: number;
  month_short: string;
  month_long: string;
}

interface ColorOptions {
  data1?: string;
  data2?: string;
  data3?: string;
}

@Component({
  standalone: true,
  selector: 'app-active-business-bars',
  imports: [CommonModule, MultipleBarComponent, DropdownComponent, IndividualToasterComponent],
  templateUrl: './active-business-bars.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './active-business-bars.component.scss'
})
export class ActiveBusinessBarsComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() dateRange!: DateRangePickerOutput;
  @Input() productServerName: string = 'denefits';
  @Input() requiresAccessToken: boolean = false;
  @Input() cocuId: number = 1;
  @Input() countryId: number = 1;
  @ViewChild('yearDropdown') yearDropdown?: DropdownComponent;
  @ViewChild('monthDropdown') monthDropdown?: DropdownComponent;
  isLoading = signal(false)
  private currentTheme: Theme = 'light';
  isDark: boolean = this.currentTheme === 'dark';
  // Dropdown options
  yearOptions: DropdownOption[] = [];
  monthOptions: DropdownOption[] = [
    { value: 'all', label: 'All Months' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  // Selected values with proper initialization
  selectedYears: string[] = [];
  selectedMonth: string[] = [];
  
  // Initial values for dropdowns (to be bound in template)
  initialSelectedYears: string[] = [];
  initialSelectedMonth: string[] = [];

  // Chart data
  chartData1: any[] = [];
  chartData2: any[] = [];
  chartData3: any[] = [];
  legendData: any[] = [];

  // Chart colors
  colors: ColorOptions = {};

  // Store API data organized by year
  apiDataByYear: Map<number, YearData> = new Map();

  constructor(
    private apiService: ApiService,
    private themeService: ThemeService
  ) {}
 
  ngOnInit(): void {
    this.generateYearOptions();
    this.initializeDefaultSelection();
    this.fetchDataForSelectedYears();
    this.themeService.getThemeObservable().subscribe((theme: Theme) => {
      this.isDark = theme === 'dark';
      this.colors = {
        data1: this.isDark ? '#f472b6' : '#b927ff' as const,
        data2: this.isDark ? '#a78bfa' : '#8b5cf6' as const,
        data3: this.isDark ? '#818cf8' : '#6366f1' as const 
      };
      this.loadChartData();
    });
  }

  ngAfterViewInit(): void {
    // Set initial values in the dropdowns after view is initialized
    setTimeout(() => {
      if (this.yearDropdown) {
        this.yearDropdown.writeValue(this.initialSelectedYears);
      }
      if (this.monthDropdown) {
        this.monthDropdown.writeValue(this.initialSelectedMonth);
      }
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['productServerName'] && !changes['productServerName'].firstChange) || 
        (changes['dateRange'] && !changes['dateRange'].firstChange) ||
        (changes['cocuId'] && !changes['cocuId'].firstChange) ||
        (changes['countryId'] && !changes['countryId'].firstChange)) {
      this.fetchDataForSelectedYears();
    }
  }

  private generateYearOptions(): void {
    const currentYear = new Date().getFullYear();
    const startYear = 2017;
    
    this.yearOptions = [];
    for (let year = startYear; year <= currentYear; year++) {
      this.yearOptions.push({
        value: year.toString(),
        label: year.toString()
      });
    }
  }

  private initializeDefaultSelection(): void {
    // Select the 3 most recent years by default
    const currentYear = new Date().getFullYear();
    this.selectedYears = [
      (currentYear - 2).toString(),
      (currentYear - 1).toString(),
      currentYear.toString()
    ];

    // Set initial values for the dropdown to highlight them
    this.initialSelectedYears = [...this.selectedYears];

    // All 12 months selected by default — keeps both the data layer and the
    // dropdown UI in sync so the trigger shows "All months selected" and every
    // checkbox in the menu appears ticked on first load.
    this.selectedMonth = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
    this.initialSelectedMonth = [...this.selectedMonth];
  }

  onYearChange(selectedValues: string | string[]): void {
    // selectionChange returns string[] for multiple mode
    this.selectedYears = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
    
    // Limit to max 3 years
    if (this.selectedYears.length > 3) {
      this.selectedYears = this.selectedYears.slice(0, 3);
    }
    
    this.fetchDataForSelectedYears();
  }

  onMonthChange(selectedValue: string | string[]): void {
    // selectionChange returns string[] for multiple mode
    this.selectedMonth = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
    this.fetchDataForSelectedYears();
  }

  // Custom trigger label for the month dropdown:
  //  - 0 selected   -> placeholder ("All Months")
  //  - 1-2 selected -> "January, February"
  //  - 3-11         -> "{count} Months"
  //  - 12 selected  -> "All months selected"
  monthDisplayFormatter = (
    selectedOptions: DropdownOption[],
    placeholder: string
  ): string => {
    const realOptions = selectedOptions.filter((o) => o.value !== 'all');
    const count = realOptions.length;

    if (count === 0) return placeholder;
    if (count === 12) return 'All Months Selected';
    if (count <= 2) return realOptions.map((o) => o.label).join(', ');
    return `${count} Months Selected`;
  };

  private fetchDataForSelectedYears(): void {
    if (this.selectedYears.length === 0) {
      this.loadChartData();
      return;
    }

    this.fetchActiveBusinessDataForYear();
  }

  private fetchActiveBusinessDataForYear(year?: any): void {
this.isLoading.set(true)
    this.apiDataByYear.clear();
    const payload: any = {
      years: this.selectedYears,
      months: this.selectedMonth,
      cocu_id: this.cocuId,
      country_id: this.countryId
    };

    if (this.productServerName === 'credee') {
      payload['timezone'] = 'PDT';
    }

    this.apiService
      .post('/visionboard/stats/active_range_business', payload, {
        addAccessToken: this.requiresAccessToken,
        productServerName: this.productServerName,
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
      })
      .subscribe({
        next: (response: any) => {
          if (response?.is_error === 0 && response?.finalData) {
            this.isLoading.set(false)
            
            
            // Clear previous data
            this.apiDataByYear.clear();
            
            // Process finalData - it should be an array of year objects
            response.finalData.forEach((yearDataItem: any) => {
              const year = yearDataItem.year;
              
              const yearData: YearData = {
                year: year,
                stats: {
                  total: yearDataItem.stats?.total || 0,
                  dental: yearDataItem.stats?.dental || 0,
                  healthcare: yearDataItem.stats?.healthcare || 0,
                  non_healthcare: yearDataItem.stats?.non_healthcare || 0
                },
                data: (yearDataItem.data || []).map((record: any) => ({
                  start: record.start,
                  end: record.end,
                  total: record.total || 0,
                  dental: record.dental || 0,
                  healthcare: record.healthcare || 0,
                  non_healthcare: record.non_healthcare || 0,
                  month_short: record.month_short || this.getMonthShort(record.start),
                  month_long: record.month_long || this.getMonthLong(record.start),
                  label: record.month_short || this.getMonthShort(record.start)
                }))
              };

              // Store the data
              this.apiDataByYear.set(year, yearData);
            });
            
            // Reload chart with new data
            this.loadChartData();
          }
        },
        error: (error: any) => {
          console.error('Error fetching active business data:', error);
          this.isLoading.set(false)
        }
      });
  }

  private getMonthShort(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short' });
  }

  private getMonthLong(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long' });
  }

  private loadChartData(): void {
    // Get data from API for selected years
    const selectedYearData: YearData[] = this.selectedYears
      .sort()
      .map(year => this.apiDataByYear.get(parseInt(year)))
      .filter((data): data is YearData => data !== undefined)
      .slice(0, 3); // Limit to 3 years maximum

    if (selectedYearData.length === 0) {
      this.chartData1 = [];
      this.chartData2 = [];
      this.chartData3 = [];
      this.legendData = [];
      return;
    } 

    // Filter data by selected month(s) if applicable
    const filterByMonth = (data: MonthData[]): MonthData[] => {
      if (!this.selectedMonth || this.selectedMonth.length === 0 || this.selectedMonth.length === 12) {
        // Show all months when nothing is selected or all 12 months are selected
        return data;
      }
      
      // If specific months are selected, filter the data
      return data.filter((monthData) => {
        const monthNum = new Date(monthData.start).getMonth() + 1; // 1-12
        return this.selectedMonth.includes(monthNum.toString());
      });
    };


    // Assign data to chart datasets (up to 3 years)
    // Apply month filter to each dataset
    this.chartData1 = selectedYearData[0] ? filterByMonth(selectedYearData[0].data) : [];
    this.chartData2 = selectedYearData[1] ? filterByMonth(selectedYearData[1].data) : [];
    this.chartData3 = selectedYearData[2] ? filterByMonth(selectedYearData[2].data) : [];


    // Update legend
    this.legendData = selectedYearData.map((yearData, index) => ({
      label: yearData.year.toString(),
      color: index === 0 ? this.colors?.data1 : index === 1 ? this.colors?.data2 : this.colors?.data3
    }));

  }

}
